import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  MoreVertical, 
  Play, 
  Pause, 
  Edit, 
  Trash2, 
  Mic,
  CheckCircle,
  Clock,
  AlertCircle,
  Users,
  Zap,
  Globe,
  Volume2
} from 'lucide-react';

import { voiceApi, Voice, VoiceSettings, textToSpeechApi } from '../../services/audioApi';
import axios from 'axios';
import CreateVoiceModal from '../../components/Audio/CreateVoiceModal';
import EditVoiceModal from '../../components/Audio/EditVoiceModal';
import DeleteConfirmModal from '../../components/Common/DeleteConfirmModal';
import ImportElevenLabsModal from '../../components/Audio/ImportElevenLabsModal';
import ImportSarvamModal from '../../components/Audio/ImportSarvamModal';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { PermissionGuard } from '../../components/Auth/PermissionGuard';
import { formatDistanceToNow } from 'date-fns';
import Pagination from '@/components/Common/Pagination';

const VoiceManagement: React.FC = () => {
  const [voices, setVoices] = useState<Voice[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [languageFilter, setLanguageFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showSarvamImportModal, setShowSarvamImportModal] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState<Voice | null>(null);
  const [playingVoice, setPlayingVoice] = useState<string | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [previewingVoice, setPreviewingVoice] = useState<string | null>(null);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState<string | null>(null);
  
  // Fetch voices
  const fetchVoices = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 12,
        search: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        language: languageFilter !== 'all' ? languageFilter : undefined,
      };

      const response = await voiceApi.getVoices(params);
      setVoices(response.data.voices);
      setTotalPages(response.data.pagination.pages);
    } catch (error) {
      console.error('Failed to fetch voices:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch voice statistics
  const fetchStats = async () => {
    try {
      const response = await voiceApi.getVoiceStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch voice stats:', error);
    }
  };

  useEffect(() => {
    fetchVoices();
  }, [currentPage, searchTerm, statusFilter, languageFilter]);

  useEffect(() => {
    fetchStats();
  }, []);

  // Handle voice creation
  const handleVoiceCreated = () => {
    setShowCreateModal(false);
    fetchVoices();
    fetchStats();
  };

  // Handle voice update
  const handleVoiceUpdated = () => {
    setShowEditModal(false);
    setSelectedVoice(null);
    fetchVoices();
  };

  const handleVoicePreview = async (voice: Voice) => {
    try {
      if (audioElement) {
        audioElement.pause();
        URL.revokeObjectURL(audioElement.src);
        setPlayingVoice(null);
        setAudioElement(null);
      }

      if (previewingVoice === voice._id) {
        setPreviewingVoice(null);
        return;
      }

      setIsGeneratingPreview(voice._id);

      const previewText = `Hello, this is a preview of the ${voice.name} voice.`;

      const startRes = await textToSpeechApi.generateSpeech({
        text: previewText,
        voiceId: voice._id,
      });

      if (!startRes.success) throw new Error('Failed to start TTS job');
      const jobId: string = startRes.data.jobId;

      // Poll until completed (max 30 seconds)
      let jobData: any = null;
      for (let i = 0; i < 30; i++) {
        await new Promise((r) => setTimeout(r, 1000));
        const poll = await textToSpeechApi.getTtsJob(jobId);
        if (poll.data.status === 'completed') { jobData = poll.data; break; }
        if (poll.data.status === 'failed') throw new Error(poll.data.error || 'TTS generation failed');
      }

      if (!jobData) throw new Error('TTS job timed out');

      // Fetch audio as blob (sends auth headers)
      const token = localStorage.getItem('token');
      const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      const blobRes = await axios.get(`${apiBase}/audio/text-to-speech/${jobId}/download`, {
        responseType: 'blob',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      const objectUrl = URL.createObjectURL(blobRes.data);
      const audio = new Audio(objectUrl);

      audio.onended = () => {
        setPreviewingVoice(null);
        setPlayingVoice(null);
        setAudioElement(null);
        URL.revokeObjectURL(objectUrl);
      };

      audio.onerror = () => {
        setPreviewingVoice(null);
        setPlayingVoice(null);
        setAudioElement(null);
        URL.revokeObjectURL(objectUrl);
      };

      setAudioElement(audio);
      setPreviewingVoice(voice._id);
      setPlayingVoice(voice._id);
      audio.play();
    } catch (error) {
      console.error('Failed to generate voice preview:', error);
    } finally {
      setIsGeneratingPreview(null);
    }
  };

  // Handle voice deletion
  const handleDeleteVoice = async () => {
    if (!selectedVoice) return;

    try {
      await voiceApi.deleteVoice(selectedVoice._id);
      setShowDeleteModal(false);
      setSelectedVoice(null);
      fetchVoices();
      fetchStats();
    } catch (error) {
      console.error('Failed to delete voice:', error);
    }
  };

  // Get status badge
  const getStatusBadge = (status: string, progress?: number) => {
    const badges = {
      training: (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">
          <Clock className="w-3 h-3 mr-1" />
          Training {progress ? `${progress}%` : ''}
        </span>
      ),
      ready: (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
          <CheckCircle className="w-3 h-3 mr-1" />
          Ready
        </span>
      ),
      failed: (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
          <AlertCircle className="w-3 h-3 mr-1" />
          Failed
        </span>
      ),
    };
    return badges[status as keyof typeof badges] || null;
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Get total sample size for a voice
  const getTotalSampleSize = (voice: Voice) => {
    return voice.sampleFiles.reduce((total, file) => total + file.size, 0);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Voice Management</h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">Create and manage your custom voice clones</p>
            </div>
            <div className="flex items-center gap-3">
              <PermissionGuard module="voices" action="create">
                <button
                  onClick={() => setShowImportModal(true)}
                  className="inline-flex items-center px-4 py-2 bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 border border-blue-600 dark:border-blue-500 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                >
                  <Zap className="w-5 h-5 mr-2" />
                  Import from ElevenLabs
                </button>
              </PermissionGuard>
              <PermissionGuard module="voices" action="create">
                <button
                  onClick={() => setShowSarvamImportModal(true)}
                  className="inline-flex items-center px-4 py-2 bg-white dark:bg-gray-800 text-green-600 dark:text-green-400 border border-green-600 dark:border-green-500 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                >
                  <Volume2 className="w-5 h-5 mr-2" />
                  Import from Sarvam
                </button>
              </PermissionGuard>
              <PermissionGuard module="voices" action="create">
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Create Voice
                </button>
              </PermissionGuard>
            </div>
          </div>

          {/* Statistics */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Voices</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalVoices}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Ready Voices</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.readyVoices}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                <div className="flex items-center">
                  <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                    <Clock className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Training</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.trainingVoices}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <Play className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Usage</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalUsage}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search voices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="ready">Ready</option>
              <option value="training">Training</option>
              <option value="failed">Failed</option>
            </select>
            <select
              value={languageFilter}
              onChange={(e) => setLanguageFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Languages</option>
              <option value="en">English</option>
              <option value="hi">Hindi</option>
              <option value="bn">Bengali</option>
              <option value="te">Telugu</option>
              <option value="mr">Marathi</option>
              <option value="ta">Tamil</option>
            </select>
          </div>
        </div>

        {/* Voice Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : voices.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No voices found</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {searchTerm || statusFilter !== 'all' || languageFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Create your first voice clone to get started'}
            </p>
            {!searchTerm && statusFilter === 'all' && languageFilter === 'all' && (
              <PermissionGuard module="voices" action="create">
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Create Voice
                </button>
              </PermissionGuard>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {voices.map((voice) => (
              <div key={voice._id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{voice.name}</h3>
                      {voice.provider === 'ai4bharat' ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300">
                          <Globe className="w-3 h-3 mr-1" /> AI4Bharat
                        </span>
                      ) : voice.provider === 'sarvam' ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                          <Volume2 className="w-3 h-3 mr-1" /> Sarvam
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                          <Zap className="w-3 h-3 mr-1" /> ElevenLabs
                        </span>
                      )}
                    </div>
                    {voice.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{voice.description}</p>
                    )}
                    {getStatusBadge(voice.status, voice.trainingProgress)}
                  </div>
                  <div className="relative">
                    <button
                      className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => setSelectedVoice(voice)}
                    >
                      <MoreVertical className="w-5 h-5" />
                    </button>
                    {selectedVoice?._id === voice._id && (
                      <div className="absolute right-0 top-10 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-10 min-w-[160px]">
                        <PermissionGuard module="voices" action="update">
                          <button
                            onClick={() => {
                              setShowEditModal(true);
                              setSelectedVoice(voice);
                            }}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Edit Voice
                          </button>
                        </PermissionGuard>
                        <PermissionGuard module="voices" action="delete">
                          <button
                            onClick={() => setShowDeleteModal(true)}
                            className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Voice
                          </button>
                        </PermissionGuard>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Language:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{voice.language.toUpperCase()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Samples:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{voice.sampleFiles.length}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Total Size:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{formatFileSize(getTotalSampleSize(voice))}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Usage:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{voice.usageCount} times</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Created:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatDistanceToNow(new Date(voice.createdAt), { addSuffix: true })}
                    </span>
                  </div>
                </div>

                {voice.status === 'ready' && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                        className={`w-full flex items-center justify-center px-4 py-2 rounded-lg transition-colors ${
                            isGeneratingPreview === voice._id
                            ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                            : playingVoice === voice._id
                            ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40'
                            : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/40'
                        }`}
                        onClick={() => handleVoicePreview(voice)}
                        disabled={isGeneratingPreview === voice._id}
                        >
                        {isGeneratingPreview === voice._id ? (
                            <>
                            <LoadingSpinner size="sm" className="mr-2" />
                            Generating...
                            </>
                        ) : playingVoice === voice._id ? (
                            <>
                            <Pause className="w-4 h-4 mr-2" />
                            Stop Preview
                            </>
                        ) : (
                            <>
                            <Play className="w-4 h-4 mr-2" />
                            Preview Voice
                            </>
                        )}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} className="mt-8" />
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateVoiceModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onVoiceCreated={handleVoiceCreated}
        />
      )}

      {showEditModal && selectedVoice && (
        <EditVoiceModal
          isOpen={showEditModal}
          voice={selectedVoice}
          onClose={() => {
            setShowEditModal(false);
            setSelectedVoice(null);
          }}
          onVoiceUpdated={handleVoiceUpdated}
        />
      )}

      {showDeleteModal && selectedVoice && (
        <DeleteConfirmModal
          isOpen={showDeleteModal}
          title="Delete Voice"
          message={`Are you sure you want to delete "${selectedVoice.name}"? This action cannot be undone.`}
          onConfirm={handleDeleteVoice}
          onCancel={() => {
            setShowDeleteModal(false);
            setSelectedVoice(null);
          }}
        />
      )}

      <ImportElevenLabsModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImported={() => {
          fetchVoices();
          fetchStats();
        }}
      />

      <ImportSarvamModal
        isOpen={showSarvamImportModal}
        onClose={() => setShowSarvamImportModal(false)}
        onImported={() => {
          fetchVoices();
          fetchStats();
        }}
      />
    </div>
  );
};

export default VoiceManagement;
